/**
 * AccessibilityMenu — floating button + popover that exposes the
 * high-contrast, large-text, and reduce-motion toggles wired up by
 * `useAccessibility`. Bilingual labels follow the current language.
 */

import { useAccessibility } from '@/hooks/useAccessibility';
import { useLanguage } from '@/hooks/useLanguage';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { Accessibility, Contrast, Type, Activity, RotateCcw, Mic, MicOff } from 'lucide-react';

interface SpeechRecognitionEventLike {
  results: ArrayLike<{ 0: { transcript: string } }>;
}

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  stop: () => void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionLike;
}

type SpeechWindow = Window & {
  SpeechRecognition?: SpeechRecognitionConstructor;
  webkitSpeechRecognition?: SpeechRecognitionConstructor;
};

export function AccessibilityMenu(): JSX.Element {
  const { prefs, toggle, reset } = useAccessibility();
  const { language, isRTL } = useLanguage();
  const [, navigate] = useLocation();
  const [isListening, setIsListening] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState('');
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);

  const t = (en: string, ar: string) => (language === 'ar' ? ar : en);

  useEffect(() => () => recognitionRef.current?.stop(), []);

  const runVoiceCommand = (transcript: string): void => {
    const command = transcript.toLocaleLowerCase(language === 'ar' ? 'ar' : 'en').trim();
    const destinations: Array<[string[], string]> = [
      [['home', 'dashboard', 'الرئيسية', 'لوحة التحكم'], '/'],
      [['appointments', 'المواعيد'], '/appointments'],
      [['medications', 'الأدوية'], '/medications'],
      [['medical records', 'records', 'السجلات الطبية'], '/records'],
      [['settings', 'الإعدادات'], '/settings'],
    ];
    const destination = destinations.find(([phrases]) =>
      phrases.some((phrase) => command.includes(phrase))
    );
    if (destination) {
      navigate(destination[1]);
      setVoiceStatus(t(`Opening ${destination[1]}`, `فتح ${destination[1]}`));
      return;
    }
    if (command.includes('large text') || command.includes('نص كبير')) {
      toggle('largeText');
      setVoiceStatus(t('Large text toggled', 'تم تبديل النص الكبير'));
      return;
    }
    if (command.includes('high contrast') || command.includes('تباين')) {
      toggle('highContrast');
      setVoiceStatus(t('High contrast toggled', 'تم تبديل التباين العالي'));
      return;
    }
    setVoiceStatus(t('Command not recognized', 'لم يتم التعرف على الأمر'));
  };

  const toggleVoiceCommands = (): void => {
    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }
    const speechWindow = window as SpeechWindow;
    const Recognition = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
    if (!Recognition) {
      setVoiceStatus(
        t(
          'Voice commands are not supported by this browser.',
          'الأوامر الصوتية غير مدعومة في هذا المتصفح.'
        )
      );
      return;
    }
    const recognition = new Recognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = language === 'ar' ? 'ar-IQ' : 'en-US';
    recognition.onresult = (event) => runVoiceCommand(event.results[0][0].transcript);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => {
      setIsListening(false);
      setVoiceStatus(t('Voice command could not be heard.', 'تعذر سماع الأمر الصوتي.'));
    };
    recognitionRef.current = recognition;
    setVoiceStatus(t('Listening for a command', 'جارٍ الاستماع إلى أمر'));
    setIsListening(true);
    recognition.start();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className={`border-medical-primary hover:bg-medical-primary/10 fixed bottom-24 z-40 h-12 w-12 rounded-full border-2 bg-white shadow-lg lg:bottom-6 ${isRTL ? 'right-4 lg:right-24' : 'left-4 lg:left-6'}`}
          aria-label={t('Accessibility settings', 'إعدادات إمكانية الوصول')}
          data-testid="button-open-accessibility-menu"
        >
          <Accessibility className="text-medical-primary h-6 w-6" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className={`w-80 ${isRTL ? 'rtl font-cairo' : 'ltr'}`}
        side="top"
        align="start"
        data-testid="popover-accessibility-menu"
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">
            {t('Accessibility', 'إمكانية الوصول')}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={reset}
            data-testid="button-reset-accessibility"
          >
            <RotateCcw className="mr-1 h-3 w-3" />
            {t('Reset', 'إعادة تعيين')}
          </Button>
        </div>

        <p className="mb-4 text-xs text-gray-500">
          {t(
            'Adjustments are saved on this device and applied immediately.',
            'تُحفظ التعديلات على هذا الجهاز وتُطبَّق فوراً.'
          )}
        </p>

        <div className="space-y-3">
          <ToggleRow
            icon={<Contrast className="text-medical-primary h-4 w-4" />}
            label={t('High contrast', 'تباين عالٍ')}
            description={t(
              'Stronger colors and bolder borders for low-vision readers.',
              'ألوان أقوى وحدود أوضح لمن يعانون ضعف البصر.'
            )}
            checked={prefs.highContrast}
            onToggle={() => toggle('highContrast')}
            testId="switch-high-contrast"
          />
          <ToggleRow
            icon={<Type className="text-medical-primary h-4 w-4" />}
            label={t('Large text', 'نص كبير')}
            description={t(
              'Increase the base font size by 25% across the app.',
              'زيادة حجم الخط الأساسي بنسبة 25%.'
            )}
            checked={prefs.largeText}
            onToggle={() => toggle('largeText')}
            testId="switch-large-text"
          />
          <ToggleRow
            icon={<Activity className="text-medical-primary h-4 w-4" />}
            label={t('Reduce motion', 'تقليل الحركة')}
            description={t(
              'Disable non-essential animations and transitions.',
              'تعطيل الرسوم المتحركة والانتقالات غير الضرورية.'
            )}
            checked={prefs.reduceMotion}
            onToggle={() => toggle('reduceMotion')}
            testId="switch-reduce-motion"
          />
          <div className="rounded-lg border border-gray-100 p-3">
            <Button
              type="button"
              variant={isListening ? 'destructive' : 'outline'}
              className="w-full"
              onClick={toggleVoiceCommands}
              aria-pressed={isListening}
              aria-describedby="voice-command-help voice-command-status"
              data-testid="button-voice-commands"
            >
              {isListening ? <MicOff aria-hidden="true" /> : <Mic aria-hidden="true" />}
              {isListening ? t('Stop listening', 'إيقاف الاستماع') : t('Voice command', 'أمر صوتي')}
            </Button>
            <p id="voice-command-help" className="mt-2 text-xs text-gray-600">
              {t(
                'Say “appointments”, “medications”, “settings”, “large text”, or “high contrast”.',
                'قل «المواعيد» أو «الأدوية» أو «الإعدادات» أو «نص كبير» أو «تباين».'
              )}
            </p>
            <p id="voice-command-status" className="sr-only" role="status" aria-live="polite">
              {voiceStatus}
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ToggleRow({
  icon,
  label,
  description,
  checked,
  onToggle,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onToggle: () => void;
  testId: string;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-gray-100 p-3">
      <div className="flex items-start gap-2">
        <div className="mt-0.5">{icon}</div>
        <div>
          <p className="text-sm font-medium text-gray-900">{label}</p>
          <p className="mt-0.5 text-xs text-gray-500">{description}</p>
        </div>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onToggle}
        aria-label={label}
        data-testid={testId}
      />
    </div>
  );
}
